import React, { useEffect, useState } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Button,
    Checkbox,
    VStack,
    Text,
} from '@chakra-ui/react';
import { normalizeInvoiceVendorAliasKey } from '../../utils/invoiceVendorDisplay';

export interface VendorAliasPromptContext {
    nexVendorName: string;
    /** Invoice `supplierText` before the user picked the Nexsyis vendor (saved as a text-on-invoice alias). */
    invoiceVendorSnapshot: string;
    internalSupplierId?: string | null;
    /** NEX PO `vendorID` for the vendor the user selected on this RO. */
    nexVendorId?: number;
}

interface SaveVendorNexAliasPromptModalProps {
    isOpen: boolean;
    onClose: () => void;
    context: VendorAliasPromptContext | null;
    onConfirm: (aliasesToAdd: Array<{ invoiceAlias: string; nexVendorName: string }>) => void;
    isSaving?: boolean;
}

export default function SaveVendorNexAliasPromptModal({
    isOpen,
    onClose,
    context,
    onConfirm,
    isSaving,
}: SaveVendorNexAliasPromptModalProps) {
    const [saveInvoiceAlias, setSaveInvoiceAlias] = useState(true);

    const nex = context?.nexVendorName?.trim() ?? '';
    const inv = context?.invoiceVendorSnapshot?.trim() ?? '';

    const norm = normalizeInvoiceVendorAliasKey;
    const canOfferInvoiceAlias = Boolean(inv && norm(inv) !== norm(nex));

    useEffect(() => {
        if (isOpen && context) {
            setSaveInvoiceAlias(canOfferInvoiceAlias);
        }
    }, [isOpen, context, canOfferInvoiceAlias]);

    if (!context) return null;

    const handleSave = () => {
        if (!canOfferInvoiceAlias || !saveInvoiceAlias) return;
        onConfirm([{ invoiceAlias: inv, nexVendorName: nex }]);
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Save Vendor Alias?</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <VStack align="stretch" spacing={3}>
                        <Text fontSize="sm" color="gray.700">
                            Save invoice wording under your supplier&apos;s vendor mappings so future uploads
                            match this Nexsyis PO vendor automatically. Edit anytime under Settings → Invoicing.
                        </Text>
                        <Text fontSize="sm" fontWeight="medium">
                            Nexsyis vendor: {nex}
                        </Text>
                        {canOfferInvoiceAlias ? (
                            <Checkbox
                                isChecked={saveInvoiceAlias}
                                onChange={(e) => setSaveInvoiceAlias(e.target.checked)}
                            >
                                <Text as="span" fontSize="sm">
                                    Text on invoice: {inv}
                                </Text>
                            </Checkbox>
                        ) : (
                            <Text fontSize="sm" color="gray.600">
                                Invoice vendor text already matches this Nexsyis name; nothing new to save.
                            </Text>
                        )}
                    </VStack>
                </ModalBody>
                <ModalFooter gap={2}>
                    <Button size="action" variant="ghost" onClick={onClose} isDisabled={isSaving}>
                        Skip
                    </Button>
                    <Button
                        size="action"
                            bg="#0071E3"
                            color="white"
                            _hover={{ bg: '#3544a0' }}
                        onClick={handleSave}
                        isLoading={isSaving}
                        isDisabled={!canOfferInvoiceAlias || !saveInvoiceAlias}
                    >
                        Save
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
